import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { RootState } from 'containers/Header/types';
import { useTranslation } from 'react-i18next';
import {
  makeSelectIsSubmittingFurniture,
  makeSelectSaveModeFurniture,
} from 'containers/UserActions/selectors';
import {
  resetSaveModeAction,
  saveFurnitureRequestAction,
} from 'containers/UserActions/actions';
import { SaveMode } from '../constants';
import { FaRegShareSquare } from 'react-icons/fa';
import useMedia from 'use-media';
import { getColladaExport, getPNGimage } from '../../Viewer';
import { makeSelectFurniture } from '../../Configurator/selectors/furniture';
import { Furniture } from '../../../lib/modelBuilder/entities/Furniture';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { FurnitureEditorContext } from '../../../lib/constraintsChecker/FurnitureEditor';
import { makeSelectContext } from '../../Configurator/selectors/general';
import { isProduction } from '../../../utils/constants';

// import styled from 'styled-components/macro';

interface StateProps {
  submitting: boolean;
  saveMode?: SaveMode;
  furniture: Furniture | null;
  context: FurnitureEditorContext;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  submitting: makeSelectIsSubmittingFurniture(),
  saveMode: makeSelectSaveModeFurniture(),
  furniture: makeSelectFurniture(),
  context: makeSelectContext(),
});

const ExportFurniture = () => {
  const dispatch = useDispatch();
  const { submitting, saveMode, furniture, context } = useSelector(
    stateSelector,
  );
  const { t } = useTranslation();
  const isMobile = useMedia({ maxWidth: 1023 });
  const [colladaExport, setColladaExport] = useState<any>(undefined);
  const [image, setImage] = useState<any>(undefined);

  useEffect(() => {
    if (!submitting && furniture && saveMode === SaveMode.Export) {
      const cabinetOrFurniture =
        furniture.quantity > 1 ? furniture : furniture.cabinets[0].cabinet;
      const reference = cabinetOrFurniture.reference;
      const filename = `Kewlox-${reference}`;

      const zip = new JSZip();
      zip.file(`${filename}.dae`, colladaExport.data);
      colladaExport.textures.forEach((tex: any) =>
        zip.file(`textures/${tex.name}.${tex.ext}`, tex.data),
      );
      if (image)
        zip.file(`${filename}.png`, image.split(',')[1], { base64: true });
      zip.file(
        `${filename}.url`,
        `[InternetShortcut]\nURL=https://kewlox.com/k-${reference}\nIconFile=https://www.kewlox.com/shop/img/favicon-1.ico`,
      );

      zip.file('LICENSE.txt', '© Kewlox - All rights reserved');
      zip.file(
        'ABOUT.txt',
        `Kewlox - ${cabinetOrFurniture.names['fr']}\r\n${cabinetOrFurniture.descriptions['fr']}\r\nhttps://kewlox.com/fr/k-${reference}\r\n\r\n` +
          `Kewlox - ${cabinetOrFurniture.names['nl']}\r\n${cabinetOrFurniture.descriptions['nl']}\r\nhttps://kewlox.com/nl/k-${reference}\r\n\r\n` +
          `Kewlox - ${cabinetOrFurniture.names['en']}\r\n${cabinetOrFurniture.descriptions['en']}\r\nhttps://kewlox.com/en/k-${reference}\r\n\r\n`,
      );

      zip
        .generateAsync({ type: 'blob' /*, compression: 'DEFLATE'*/ })
        .then(function(content) {
          saveAs(content, `${filename}.zip`);
          dispatch(resetSaveModeAction());
        });

      setColladaExport(undefined);
      setImage(undefined);
    }
  }, [submitting, saveMode]);

  function startExport() {
    setColladaExport(getColladaExport());
    setImage(getPNGimage());
    dispatch(saveFurnitureRequestAction(SaveMode.Export));
  }

  if (isMobile) {
    return null;
  }

  if (!(context === FurnitureEditorContext.sales || !isProduction)) {
    return null;
  }

  return (
    <button
      className="flex items-center mx-3"
      onClick={() => {
        startExport();
      }}
      disabled={submitting}
    >
      <FaRegShareSquare className="h-5 w-5" />
      <div className="ml-2 text-lg text-gray-900">{t('Export 3D')}</div>
    </button>
  );
};

export default ExportFurniture;
